import React from 'react';
import { useDispatch } from 'react-redux';
import { Grid } from '@mui/material';

import { AuthorizerContext } from 'app/providers/acl/AclProvider';
import { useSettings, Utils } from 'app/main/components';
import CompaniesFinancialStatementsTable, {
	type CompaniesFinancialStatementField
} from '../../components/CompaniesFinancialStatementsTable';
import AtokaLinkSection from '../../components/atoka/AtokaLinkSection';
import AtokaInfoSection from '../../components/atoka/AtokaInfoSection';
import { showAtokaCompaniesSearchDialog } from '../../components/atoka/AtokaCompaniesSearchDialog';
import { showAtokaReportsDownloadDialog } from '../../components/atoka/AtokaReportsDownloadDialog';

interface CompaniesAtokaTabProps {
	compUid: string;
	atokaId: string;
	atokaUpdateDate: string;
	vatNumber: string;
	updateRefresh: VoidFunction;
}

const incomeStatementSectionFields: CompaniesFinancialStatementField[] = [
	{ name: 'atfs_production', style: 'currency', trend: 'positive' },
	{ className: 'pl-16', name: 'atfs_revenue', style: 'currency', trend: 'positive' },
	{ name: 'atfs_costs', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'atfs_purchases', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'atfs_servicesAndTPGoodsCharges', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'atfs_staffCosts', style: 'currency', trend: 'negative' },
	{ className: 'pl-16', name: 'atfs_rawMaterialsVariation', style: 'currency', trend: 'negative' },
	{ name: 'atfs_mol', style: 'currency', trend: 'positive' },
	{ name: 'atfs_profit', style: 'currency', trend: 'positive' },
	{ name: 'atfs_ebitda', style: 'currency', trend: 'positive' },
	{ name: 'atfs_assets', style: 'currency', trend: 'positive' },
	{ name: 'atfs_capitalStock', style: 'currency', trend: 'positive' },
	{ name: 'atfs_equity', style: 'currency', trend: 'positive' },
	{ name: 'atfs_netWorkingCapital', style: 'currency', trend: 'positive' },
	{ name: 'atfs_netFinancialPosition', style: 'currency', trend: 'positive' }
];

export default function CompaniesAtokaTab(props: CompaniesAtokaTabProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const authorizer = React.useContext(AuthorizerContext);
	const canUpdate = authorizer.can('update', 'companies_financialstatements_atoka');

	const [isLoading, setIsLoading] = React.useState(true);
	const [atokaId, setAtokaId] = React.useState(props.atokaId);
	const [financialStatements, setFinancialStatements] = React.useState<any[]>([]);

	function linkAtokaCompany() {
		if (!props.vatNumber) {
			dispatch(Utils.alertLocalizedMsg(t, 'ATOKA_DIALOG_SEARCH_ERROR', 'error'));
		} else {
			dispatch(
				showAtokaCompaniesSearchDialog({
					compUid: props.compUid,
					vatNumber: props.vatNumber,
					setAtokaId,
					updateRefresh: props.updateRefresh
				})
			);
		}
	}

	React.useEffect(() => {
		function downloadFinancialReports() {
			dispatch(
				showAtokaReportsDownloadDialog({
					compUid: props.compUid,
					atokaId: props.atokaId,
					updateRefresh: props.updateRefresh
				})
			);
		}

		document.addEventListener('onCompaniesAtokaReportsDownload', downloadFinancialReports);
		return () => document.removeEventListener('onCompaniesAtokaReportsDownload', downloadFinancialReports);
	}, [dispatch, props.compUid, props.atokaId, props.updateRefresh]);

	React.useEffect(() => {
		setIsLoading(true);
		Utils.executeStored({
			action: 'st_companies_financialstatements_atoka_list',
			dispatch,
			params: {
				n_page: 1,
				n_rows: 100,
				filters: [{ filt_field: 'csfs_comp_uid', filt_clause: 'equals', filt_value: props.compUid }]
			},
			successHandler: response => {
				setFinancialStatements(response.data?.list || []);
				setIsLoading(false);
			}
		});
	}, [dispatch, props.compUid]);

	return (
		<Grid container spacing={2}>
			{canUpdate && (
				<Grid size={12}>
					{atokaId ? (
						<AtokaInfoSection
							atokaId={atokaId}
							lastImportDate={props.atokaUpdateDate}
							linkAtokaCompany={linkAtokaCompany}
							isLoading={isLoading}
						/>
					) : (
						<AtokaLinkSection linkAtokaCompany={linkAtokaCompany} isLoading={isLoading} />
					)}
				</Grid>
			)}
			{financialStatements.length > 0 && (
				<>
					<Grid size={12}>
						<CompaniesFinancialStatementsTable
							title={t('DETAILS_COMPANIES_ATOKA_SECTION_INCOME_STATEMENT')}
							fields={incomeStatementSectionFields}
							financialStatements={financialStatements}
							dateField="atfs_date"
						/>
					</Grid>
				</>
			)}
		</Grid>
	);
}
